import { cn } from '@/lib/utils';
import { useTimeInZone } from '@/hooks/useClosedLoopAnalytics';
import { MetricInfoButton } from '@/components/MetricInfoButton';

interface ClosedLoopZoneChartProps {
  className?: string;
}

const ZONE_COLORS: Record<number, string> = {
  1: 'hsl(170, 80%, 45%)',
  2: 'hsl(160, 70%, 50%)',
  3: 'hsl(50, 80%, 55%)',
  4: 'hsl(35, 85%, 55%)',
  5: 'hsl(15, 85%, 55%)',
  6: 'hsl(350, 80%, 55%)',
  7: 'hsl(280, 65%, 60%)',
};

const ZONE_NAMES: Record<number, string> = {
  1: 'Recovery',
  2: 'Endurance',
  3: 'Tempo',
  4: 'Threshold',
  5: 'VO2max',
  6: 'Anaerobic',
  7: 'Neuromuscular',
};

export function ClosedLoopZoneChart({ className }: ClosedLoopZoneChartProps) {
  const { data, isLoading } = useTimeInZone();

  if (isLoading) {
    return (
      <div className={cn('p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]', className)}>
        <p className="text-xs font-mono text-muted-foreground text-center animate-pulse">LOADING ZONE DATA...</p>
      </div>
    );
  }

  if (!data || !data.zone_percentages || Object.keys(data.zone_percentages).length === 0) {
    return null;
  }

  // Parse zone data — keys may be "1", "2", etc. or "Z1", "Z2", etc.
  const zones = Object.entries(data.zone_percentages)
    .map(([key, pct]) => {
      const num = parseInt(key.replace(/\D/g, ''), 10);
      return {
        zone: num,
        pct: (pct as number) * 100,
        minutes: (data.zone_minutes?.[key] as number) ?? 0,
        color: ZONE_COLORS[num] ?? 'hsl(210, 30%, 50%)',
        name: ZONE_NAMES[num] ?? `Zone ${num}`,
      };
    })
    .sort((a, b) => a.zone - b.zone);

  const totalMinutes = data.total_minutes ?? 0;

  return (
    <div className={cn(
      'p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
          <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
            TIME IN ZONE
          </span>
          <MetricInfoButton metricKey="timeInZone" />
        </div>
        <span className="text-[9px] font-mono text-white/20 uppercase tracking-wider">
          {totalMinutes > 0 ? `${Math.round(totalMinutes)} MIN` : ''}
        </span>
      </div>

      {/* Stacked horizontal bar (full width) */}
      <div className="h-6 rounded-md overflow-hidden flex mb-4">
        {zones.map((z) => (
          <div
            key={z.zone}
            className="h-full transition-all duration-700 first:rounded-l-md last:rounded-r-md"
            style={{
              width: `${Math.max(z.pct, 1)}%`,
              backgroundColor: z.color,
              opacity: z.pct > 0 ? 1 : 0.3,
            }}
            title={`Z${z.zone} ${z.name}: ${z.pct.toFixed(1)}%`}
          />
        ))}
      </div>

      {/* Zone rows */}
      <div className="space-y-2">
        {zones.map((z) => (
          <div key={z.zone} className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-sm flex-shrink-0"
              style={{ backgroundColor: z.color }}
            />
            <span className="text-[10px] font-mono tracking-wider text-[#A0A0A0] w-20 flex-shrink-0">
              Z{z.zone} · {z.name.toUpperCase().slice(0, 6)}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${z.pct}%`,
                  backgroundColor: z.color,
                  boxShadow: `0 0 6px ${z.color}30`,
                }}
              />
            </div>
            <span className="text-[10px] font-mono tabular-nums text-foreground w-10 text-right">
              {z.pct.toFixed(1)}%
            </span>
            <span className="text-[10px] font-mono tabular-nums text-white/30 w-12 text-right">
              {z.minutes > 0 ? `${Math.round(z.minutes)}m` : '—'}
            </span>
          </div>
        ))}
      </div>

      {/* Dominant zone footer */}
      {data.dominant_zone != null && (
        <div className="mt-3 pt-3 border-t border-white/[0.06]">
          <p className="text-[9px] font-mono text-white/25 tracking-wider text-center uppercase">
            [SYS] DOMINANT ZONE: Z{data.dominant_zone} · {ZONE_NAMES[data.dominant_zone] ?? 'Unknown'}
          </p>
        </div>
      )}
    </div>
  );
}
